import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, BarChart3, Printer, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { openPrintWindow, buildPrintHTML } from '../../utils/printUtils';
import { exportToExcel } from '../../utils/excelExport';

const BranchComparisonReport = () => {
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [selectedBranchIds, setSelectedBranchIds] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/branches').then(r => setBranches(r.data)).catch(() => {}); }, []);

  const toggleBranchSelection = (branchId) => setSelectedBranchIds(prev => prev.includes(branchId) ? prev.filter(id => id !== branchId) : [...prev, branchId]);
  const selectAllBranches = () => setSelectedBranchIds(branches.map(b => b.id));
  const clearBranchSelection = () => setSelectedBranchIds([]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedBranchIds.length > 0 && selectedBranchIds.length < branches.length) params.branch_ids = selectedBranchIds.join(',');
      const resp = await api.get('/reports/branch-comparison', { params });
      setData(resp.data);
    } catch { toast.error('Failed to fetch report'); }
    finally { setLoading(false); }
  };

  const fmt = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN')}`;

  const handlePrint = () => {
    if (!data) return;
    const rows = (data.branches||[]).map((b, i) => `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}">
      <td>${b.branch_code} - ${b.branch_name}</td>
      <td style="text-align:center">${b.crp_ep_count}</td>
      <td style="text-align:center">${b.brc_mc_count}</td>
      <td style="text-align:center">${b.total_entrepreneurs}</td>
      <td style="text-align:center">${b.target_entrepreneurs}</td>
      <td style="text-align:center">${b.entrepreneurs_achieved_pct}%</td>
      <td style="text-align:center">${b.total_cef_loans}</td>
      <td style="text-align:right">${fmt(b.total_cef_amount)}</td>
      <td style="text-align:center">${b.total_cif_loans}</td>
      <td style="text-align:right">${fmt(b.total_cif_amount)}</td>
      <td style="text-align:center">${b.total_bank_loans}</td>
      <td style="text-align:right">${fmt(b.total_bank_amount)}</td>
    </tr>`).join('');
    const t = data.totals || {};
    const body = `<div class="cards">
      <div class="card"><small>Branches</small><b>${data.selected_count||0}</b></div>
      <div class="card"><small>Total Entrepreneurs</small><b>${t.total_entrepreneurs||0}</b></div>
      <div class="card"><small>CEF Loans</small><b>${t.total_cef_loans||0} — ${fmt(t.total_cef_amount)}</b></div>
      <div class="card"><small>CIF Loans</small><b>${t.total_cif_loans||0} — ${fmt(t.total_cif_amount)}</b></div>
      <div class="card"><small>Bank Loans</small><b>${t.total_bank_loans||0} — ${fmt(t.total_bank_amount)}</b></div>
    </div>
    <table><thead><tr>
      <th>Branch</th><th>CRP-EPs</th><th>BRC-MCs</th><th>Entrepreneurs</th><th>Target</th><th>Achieved %</th>
      <th>CEF #</th><th>CEF Amt</th><th>CIF #</th><th>CIF Amt</th><th>Bank #</th><th>Bank Amt</th>
    </tr></thead><tbody>${rows}
    <tr style="background:#eef2ff;font-weight:bold">
      <td>TOTAL</td>
      <td style="text-align:center">${t.crp_ep_count||0}</td>
      <td style="text-align:center">${t.brc_mc_count||0}</td>
      <td style="text-align:center">${t.total_entrepreneurs||0}</td>
      <td style="text-align:center">${t.target_entrepreneurs||0}</td>
      <td style="text-align:center">${t.entrepreneurs_achieved_pct||0}%</td>
      <td style="text-align:center">${t.total_cef_loans||0}</td>
      <td style="text-align:right">${fmt(t.total_cef_amount)}</td>
      <td style="text-align:center">${t.total_cif_loans||0}</td>
      <td style="text-align:right">${fmt(t.total_cif_amount)}</td>
      <td style="text-align:center">${t.total_bank_loans||0}</td>
      <td style="text-align:right">${fmt(t.total_bank_amount)}</td>
    </tr>
    </tbody></table>`;
    openPrintWindow(buildPrintHTML('Branch Comparison Report', body));
  };

  const handleExcel = () => {
    if (!data) { toast.error('Generate the report first'); return; }
    const rows = (data.branches || []).map(b => ({
      'Branch Code': b.branch_code,
      'Branch Name': b.branch_name,
      'CRP-EPs': b.crp_ep_count || 0,
      'BRC-MCs': b.brc_mc_count || 0,
      'Entrepreneurs': b.total_entrepreneurs || 0,
      'Target': b.target_entrepreneurs || 0,
      'Achieved %': b.entrepreneurs_achieved_pct || 0,
      'Trading': b.trading_enterprises || 0,
      'Manufacturing': b.manufacturing_enterprises || 0,
      'Service': b.service_enterprises || 0,
      'CEF #': b.total_cef_loans || 0,
      'CEF Amount': Math.round(b.total_cef_amount || 0),
      'CEF O/S': Math.round(b.total_cef_outstanding || 0),
      'CIF #': b.total_cif_loans || 0,
      'CIF Amount': Math.round(b.total_cif_amount || 0),
      'CIF O/S': Math.round(b.total_cif_outstanding || 0),
      'Bank #': b.total_bank_loans || 0,
      'Bank Amount': Math.round(b.total_bank_amount || 0),
      'Bank O/S': Math.round(b.total_bank_outstanding || 0),
    }));
    const t = data.totals || {};
    rows.push({
      'Branch Code': 'TOTAL', 'Branch Name': '',
      'CRP-EPs': t.crp_ep_count || 0, 'BRC-MCs': t.brc_mc_count || 0,
      'Entrepreneurs': t.total_entrepreneurs || 0, 'Target': t.target_entrepreneurs || 0,
      'Achieved %': t.entrepreneurs_achieved_pct || 0,
      'Trading': t.trading_enterprises || 0, 'Manufacturing': t.manufacturing_enterprises || 0, 'Service': t.service_enterprises || 0,
      'CEF #': t.total_cef_loans || 0, 'CEF Amount': Math.round(t.total_cef_amount || 0), 'CEF O/S': Math.round(t.total_cef_outstanding || 0),
      'CIF #': t.total_cif_loans || 0, 'CIF Amount': Math.round(t.total_cif_amount || 0), 'CIF O/S': Math.round(t.total_cif_outstanding || 0),
      'Bank #': t.total_bank_loans || 0, 'Bank Amount': Math.round(t.total_bank_amount || 0), 'Bank O/S': Math.round(t.total_bank_outstanding || 0),
    });
    exportToExcel({
      filename: 'WWAGS_Branch_Comparison',
      sheetName: 'Branch Comparison',
      rows,
    });
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="branch-comparison-report-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div><h1 className="text-2xl font-bold text-slate-900">Branch Comparison</h1><p className="text-sm text-slate-600">Compare multiple branches - targets, staff, enterprises & loans</p></div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Button size="sm" variant="outline" onClick={selectAllBranches}>Select All</Button>
          <Button size="sm" variant="outline" onClick={clearBranchSelection}>Clear</Button>
          <span className="text-xs text-slate-500">{selectedBranchIds.length} selected</span>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {branches.map(b => (
            <button key={b.id} onClick={() => toggleBranchSelection(b.id)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${selectedBranchIds.includes(b.id) ? 'bg-indigo-100 border-indigo-300 text-indigo-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              {b.code} - {b.name}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchReport} className="bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="generate-branch-comparison">Generate Report</Button>
          {data && <Button variant="outline" onClick={handlePrint}><Printer className="w-4 h-4 mr-1" />Print</Button>}
          {data && <Button variant="outline" onClick={handleExcel} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>}
        </div>
      </div>

      {loading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700"></div></div>}

      {data && !loading && (
        <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-indigo-700 text-white">
              <tr>
                <th className="px-3 py-2 text-left">Branch</th>
                <th className="px-3 py-2 text-center">CRP-EPs</th>
                <th className="px-3 py-2 text-center">BRC-MCs</th>
                <th className="px-3 py-2 text-center">Entrepreneurs</th>
                <th className="px-3 py-2 text-center">Target</th>
                <th className="px-3 py-2 text-center">Achieved %</th>
                <th className="px-3 py-2 text-center">Trading</th>
                <th className="px-3 py-2 text-center">Manufacturing</th>
                <th className="px-3 py-2 text-center">Service</th>
                <th className="px-3 py-2 text-center bg-emerald-700">CEF #</th>
                <th className="px-3 py-2 text-right bg-emerald-700">CEF Amt</th>
                <th className="px-3 py-2 text-center bg-amber-700">CIF #</th>
                <th className="px-3 py-2 text-right bg-amber-700">CIF Amt</th>
                <th className="px-3 py-2 text-right bg-amber-700">CIF O/S</th>
                <th className="px-3 py-2 text-center bg-rose-700">Bank #</th>
                <th className="px-3 py-2 text-right bg-rose-700">Bank Amt</th>
                <th className="px-3 py-2 text-right bg-rose-700">Bank O/S</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data.branches||[]).map((b, i) => (
                <tr key={b.branch_id} className={i % 2 === 0 ? '' : 'bg-slate-50'}>
                  <td className="px-3 py-2 font-medium">{b.branch_code} - {b.branch_name}</td>
                  <td className="px-3 py-2 text-center">{b.crp_ep_count}</td>
                  <td className="px-3 py-2 text-center">{b.brc_mc_count}</td>
                  <td className="px-3 py-2 text-center font-bold">{b.total_entrepreneurs}</td>
                  <td className="px-3 py-2 text-center">{b.target_entrepreneurs}</td>
                  <td className="px-3 py-2 text-center"><span className={`font-bold ${b.entrepreneurs_achieved_pct >= 100 ? 'text-emerald-700' : b.entrepreneurs_achieved_pct >= 50 ? 'text-amber-700' : 'text-red-700'}`}>{b.entrepreneurs_achieved_pct}%</span></td>
                  <td className="px-3 py-2 text-center">{b.trading_enterprises}</td>
                  <td className="px-3 py-2 text-center">{b.manufacturing_enterprises}</td>
                  <td className="px-3 py-2 text-center">{b.service_enterprises}</td>
                  <td className="px-3 py-2 text-center bg-emerald-50">{b.total_cef_loans}</td>
                  <td className="px-3 py-2 text-right bg-emerald-50">{fmt(b.total_cef_amount)}</td>
                  <td className="px-3 py-2 text-center bg-amber-50">{b.total_cif_loans}</td>
                  <td className="px-3 py-2 text-right bg-amber-50">{fmt(b.total_cif_amount)}</td>
                  <td className="px-3 py-2 text-right bg-amber-50 text-amber-700">{fmt(b.total_cif_outstanding)}</td>
                  <td className="px-3 py-2 text-center bg-rose-50">{b.total_bank_loans}</td>
                  <td className="px-3 py-2 text-right bg-rose-50">{fmt(b.total_bank_amount)}</td>
                  <td className="px-3 py-2 text-right bg-rose-50 text-rose-700">{fmt(b.total_bank_outstanding)}</td>
                </tr>
              ))}
              {data.totals && (
                <tr className="bg-indigo-50 font-bold">
                  <td className="px-3 py-2">TOTAL</td>
                  <td className="px-3 py-2 text-center">{data.totals.crp_ep_count}</td>
                  <td className="px-3 py-2 text-center">{data.totals.brc_mc_count}</td>
                  <td className="px-3 py-2 text-center">{data.totals.total_entrepreneurs}</td>
                  <td className="px-3 py-2 text-center">{data.totals.target_entrepreneurs}</td>
                  <td className="px-3 py-2 text-center">{data.totals.entrepreneurs_achieved_pct}%</td>
                  <td className="px-3 py-2 text-center">{data.totals.trading_enterprises}</td>
                  <td className="px-3 py-2 text-center">{data.totals.manufacturing_enterprises}</td>
                  <td className="px-3 py-2 text-center">{data.totals.service_enterprises}</td>
                  <td className="px-3 py-2 text-center bg-emerald-100">{data.totals.total_cef_loans}</td>
                  <td className="px-3 py-2 text-right bg-emerald-100">{fmt(data.totals.total_cef_amount)}</td>
                  <td className="px-3 py-2 text-center bg-amber-100">{data.totals.total_cif_loans}</td>
                  <td className="px-3 py-2 text-right bg-amber-100">{fmt(data.totals.total_cif_amount)}</td>
                  <td className="px-3 py-2 text-right bg-amber-100">{fmt(data.totals.total_cif_outstanding)}</td>
                  <td className="px-3 py-2 text-center bg-rose-100">{data.totals.total_bank_loans}</td>
                  <td className="px-3 py-2 text-right bg-rose-100">{fmt(data.totals.total_bank_amount)}</td>
                  <td className="px-3 py-2 text-right bg-rose-100">{fmt(data.totals.total_bank_outstanding)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!data && !loading && <div className="text-center py-12 text-slate-500"><BarChart3 className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>Select branches and click "Generate Report"</p></div>}
    </div>
  );
};

export default BranchComparisonReport;
